import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';

interface PaymentMethodConfig {
  id: string;
  channel: 'MADA' | 'CREDIT_CARD' | 'APPLE_PAY' | 'STC_PAY' | 'BANK_TRANSFER' | 'WALLET';
  enabled: boolean;
  gateway: string | null;
  displayLabel: string;
  displayIcon: string | null;
  description: string | null;
  feePercent: number | string;
  feeFixed: number | string;
  minAmount: number | string | null;
  maxAmount: number | string | null;
  allowedTiers: string[] | null;
  allowedRoles: string[] | null;
  allowedPurposes: string[] | null;
  sortOrder: number;
}

interface SubscriptionPlan {
  id: string;
  code: string;
  label: string;
  description: string | null;
  monthlyPrice: number | string;
  yearlyPrice: number | string | null;
  monthlyListingsQuota: number;
  featuredListingsQuota: number;
  commissionShare: number | string;
  features: string[] | null;
  highlight: boolean;
  active: boolean;
  sortOrder: number;
}

interface PaymentRow {
  id: string;
  reference: string;
  userId: string;
  amount: number | string;
  fee: number | string;
  total: number | string;
  channel: string;
  purpose: string;
  status: string;
  receiptImage: string | null;
  initiatedAt: string;
}

const STATUS_COLOR: Record<string, 'amber' | 'green' | 'red' | 'navy'> = {
  PENDING: 'amber', PROCESSING: 'navy', REQUIRES_REVIEW: 'amber',
  SUCCEEDED: 'green', FAILED: 'red', REFUNDED: 'red', CANCELLED: 'red',
};

export function AdminPaymentsPage() {
  const [tab, setTab] = useState<'methods' | 'plans' | 'transactions'>('methods');

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">💳 المدفوعات والاشتراكات</h1>

      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={tab === 'methods'}      onClick={() => setTab('methods')}>⚙️ وسائل الدفع</Tab>
        <Tab active={tab === 'plans'}        onClick={() => setTab('plans')}>💎 خطط الاشتراك</Tab>
        <Tab active={tab === 'transactions'} onClick={() => setTab('transactions')}>🧾 المعاملات</Tab>
      </div>

      {tab === 'methods'      && <MethodsSection />}
      {tab === 'plans'        && <PlansSection />}
      {tab === 'transactions' && <TransactionsSection />}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ وسائل الدفع ━━━━━━━━━━━━━━━━━━━━
function MethodsSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payment-methods'],
    queryFn: () => api.get<ApiSuccess<PaymentMethodConfig[]>>('/api/admin/payment-methods'),
  });
  const [editing, setEditing] = useState<PaymentMethodConfig | null>(null);

  const toggleM = useMutation({
    mutationFn: (id: string) => api.put(`/api/admin/payment-methods/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payment-methods'] }),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  const methods = data?.data ?? [];

  return (
    <>
      {methods.length === 0 && (
        <Card className="mb-4">
          <CardBody>
            <div className="text-center py-8 text-gray-500">
              <div className="text-5xl mb-3">💳</div>
              <div className="mb-3">لا توجد وسائل دفع. شغّل seed أولاً:</div>
              <code className="bg-gray-100 px-3 py-1 rounded text-xs" dir="ltr">pnpm -C packages/db seed:payments</code>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {methods.map((m) => (
          <Card key={m.id} className={m.enabled ? '' : 'opacity-60'}>
            <CardBody>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{m.displayIcon || '💳'}</div>
                  <div>
                    <div className="font-black text-navy-dark">{m.displayLabel}</div>
                    <div className="text-xs text-gray-500">{m.channel} · {m.gateway}</div>
                  </div>
                </div>
                <Badge color={m.enabled ? 'green' : 'red'}>{m.enabled ? 'مفعّل' : 'موقوف'}</Badge>
              </div>
              {m.description && <p className="text-sm text-gray-600 mt-3">{m.description}</p>}
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-gray-500">رسوم</div>
                  <div className="font-bold">{Number(m.feePercent)}% + {Number(m.feeFixed)} ر.س</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-gray-500">حدود</div>
                  <div className="font-bold">
                    {m.minAmount ? `${Number(m.minAmount)}+` : 'لا حد'}
                    {m.maxAmount ? ` / ${Number(m.maxAmount)}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  variant={m.enabled ? 'ghost' : 'primary'}
                  className="flex-1"
                  onClick={() => toggleM.mutate(m.id)}
                  loading={toggleM.isPending}
                >
                  {m.enabled ? '⏸ تعطيل' : '▶ تفعيل'}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(m)}>⚙️</Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {editing && <MethodEditModal method={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'payment-methods'] })} />}
    </>
  );
}

function MethodEditModal({ method, onClose, onSaved }: { method: PaymentMethodConfig; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    displayLabel: method.displayLabel,
    displayIcon: method.displayIcon ?? '',
    description: method.description ?? '',
    feePercent: String(method.feePercent),
    feeFixed: String(method.feeFixed),
    minAmount: method.minAmount?.toString() ?? '',
    maxAmount: method.maxAmount?.toString() ?? '',
    sortOrder: String(method.sortOrder),
  });

  const m = useMutation({
    mutationFn: () => api.post('/api/admin/payment-methods', {
      channel: method.channel,
      enabled: method.enabled,
      gateway: method.gateway,
      displayLabel: f.displayLabel,
      displayIcon: f.displayIcon || undefined,
      description: f.description || undefined,
      feePercent: Number(f.feePercent),
      feeFixed: Number(f.feeFixed),
      minAmount: f.minAmount ? Number(f.minAmount) : null,
      maxAmount: f.maxAmount ? Number(f.maxAmount) : null,
      allowedTiers: method.allowedTiers,
      allowedRoles: method.allowedRoles,
      allowedPurposes: method.allowedPurposes,
      sortOrder: Number(f.sortOrder),
    }),
    onSuccess: () => { onSaved(); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">⚙️ تعديل {method.displayLabel}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="الاسم المعروض" value={f.displayLabel} onChange={(e) => setF({ ...f, displayLabel: e.target.value })} />
          <Input label="الأيقونة (emoji)" value={f.displayIcon} onChange={(e) => setF({ ...f, displayIcon: e.target.value })} />
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-ink mb-1">الوصف</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" />
          </div>
          <Input label="نسبة الرسوم %" type="number" value={f.feePercent} onChange={(e) => setF({ ...f, feePercent: e.target.value })} />
          <Input label="رسوم ثابتة (ر.س)" type="number" value={f.feeFixed} onChange={(e) => setF({ ...f, feeFixed: e.target.value })} />
          <Input label="الحد الأدنى" type="number" value={f.minAmount} onChange={(e) => setF({ ...f, minAmount: e.target.value })} placeholder="فارغ = بدون" />
          <Input label="الحد الأعلى" type="number" value={f.maxAmount} onChange={(e) => setF({ ...f, maxAmount: e.target.value })} placeholder="فارغ = بدون" />
          <Input label="ترتيب العرض" type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ خطط الاشتراك ━━━━━━━━━━━━━━━━━━━━
function PlansSection() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'subscription-plans'],
    queryFn: () => api.get<ApiSuccess<SubscriptionPlan[]>>('/api/subscription-plans/admin'),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/subscription-plans/admin/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'subscription-plans'] }),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  const plans = data?.data ?? [];

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ خطة جديدة</Button>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-8 text-gray-500">
              <div className="text-5xl mb-3">💎</div>
              <div className="mb-3">لا توجد خطط. شغّل seed أولاً أو أضف خطة:</div>
              <code className="bg-gray-100 px-3 py-1 rounded text-xs" dir="ltr">pnpm -C packages/db seed:payments</code>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => (
            <Card key={p.id} className={p.highlight ? 'border-2 border-gold' : ''}>
              <CardBody>
                <div className="flex items-center justify-between mb-2">
                  {p.highlight ? <Badge color="gold">⭐ الأكثر طلباً</Badge> : <span/>}
                  <Badge color={p.active ? 'green' : 'red'}>{p.active ? 'نشطة' : 'موقوفة'}</Badge>
                </div>
                <div className="text-2xl font-black text-navy-dark">{p.label}</div>
                <p className="text-xs text-gray-500" dir="ltr">{p.code}</p>
                <p className="text-sm text-gray-600 mb-3">{p.description}</p>
                <div className="text-3xl font-black text-navy-dark">
                  {Number(p.monthlyPrice).toLocaleString('en-US')} <span className="text-sm font-normal text-gray-500">ر.س/شهر</span>
                </div>
                {p.yearlyPrice && (
                  <div className="text-xs text-green-600 mt-1">
                    سنوياً: {Number(p.yearlyPrice).toLocaleString('en-US')} ر.س
                  </div>
                )}
                <ul className="mt-3 space-y-1 text-sm">
                  {(p.features ?? []).map((f, i) => <li key={i}>✓ {f}</li>)}
                </ul>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  <div>إعلانات: {p.monthlyListingsQuota}/شهر</div>
                  <div>مميزة: {p.featuredListingsQuota}</div>
                  <div>سعي المسوّق: {Number(p.commissionShare)}%</div>
                </div>
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="primary" className="flex-1" onClick={() => setEditing(p)}>✏️ تعديل</Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف خطة ${p.label}؟`)) deleteM.mutate(p.id); }}>🗑</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {(editing || openCreate) && (
        <PlanEditModal
          plan={editing}
          onClose={() => { setEditing(null); setOpenCreate(false); }}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'subscription-plans'] })}
        />
      )}
    </>
  );
}

function PlanEditModal({ plan, onClose, onSaved }: { plan: SubscriptionPlan | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    code: plan?.code ?? '',
    label: plan?.label ?? '',
    description: plan?.description ?? '',
    monthlyPrice: String(plan?.monthlyPrice ?? '0'),
    yearlyPrice: plan?.yearlyPrice?.toString() ?? '',
    monthlyListingsQuota: String(plan?.monthlyListingsQuota ?? 5),
    featuredListingsQuota: String(plan?.featuredListingsQuota ?? 0),
    commissionShare: String(plan?.commissionShare ?? 70),
    features: (plan?.features ?? []).join('\n'),
    highlight: plan?.highlight ?? false,
    active: plan?.active ?? true,
    sortOrder: String(plan?.sortOrder ?? 100),
  });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => api.post('/api/subscription-plans/admin', {
      code: f.code,
      label: f.label,
      description: f.description || undefined,
      monthlyPrice: Number(f.monthlyPrice),
      yearlyPrice: f.yearlyPrice ? Number(f.yearlyPrice) : null,
      monthlyListingsQuota: Number(f.monthlyListingsQuota),
      featuredListingsQuota: Number(f.featuredListingsQuota),
      commissionShare: Number(f.commissionShare),
      features: f.features.split('\n').map((s) => s.trim()).filter(Boolean),
      highlight: f.highlight,
      active: f.active,
      sortOrder: Number(f.sortOrder),
    }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{plan ? `✏️ تعديل ${plan.label}` : '➕ خطة جديدة'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="الكود *" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} dir="ltr" disabled={!!plan} hint={plan ? 'لا يمكن تغييره' : undefined} />
          <Input label="الاسم المعروض *" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-ink mb-1">الوصف</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" />
          </div>
          <Input label="السعر الشهري (ر.س) *" type="number" value={f.monthlyPrice} onChange={(e) => setF({ ...f, monthlyPrice: e.target.value })} />
          <Input label="السعر السنوي (ر.س)" type="number" value={f.yearlyPrice} onChange={(e) => setF({ ...f, yearlyPrice: e.target.value })} placeholder="فارغ = ضعف الشهري × 12" />
          <Input label="حد الإعلانات/شهر" type="number" value={f.monthlyListingsQuota} onChange={(e) => setF({ ...f, monthlyListingsQuota: e.target.value })} />
          <Input label="حد الإعلانات المميزة" type="number" value={f.featuredListingsQuota} onChange={(e) => setF({ ...f, featuredListingsQuota: e.target.value })} />
          <Input label="نسبة السعي للمسوّق %" type="number" value={f.commissionShare} onChange={(e) => setF({ ...f, commissionShare: e.target.value })} />
          <Input label="ترتيب العرض" type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-ink mb-1">المزايا (سطر لكل ميزة)</label>
            <textarea value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} rows={5} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" placeholder="✓ إعلانات غير محدودة" />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={f.highlight} onChange={(e) => setF({ ...f, highlight: e.target.checked })} className="w-5 h-5 accent-gold" />
            <span className="text-sm font-bold">⭐ الأكثر طلباً (مميّزة بصرياً)</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5 accent-green-600" />
            <span className="text-sm font-bold">نشطة (تظهر للمسوّقين)</span>
          </label>
        </div>
        {err && <div className="mx-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.code || !f.label}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ المعاملات ━━━━━━━━━━━━━━━━━━━━
function TransactionsSection() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payments', statusFilter],
    queryFn: () => api.get<Paginated<PaymentRow>>('/api/admin/payments/admin', { query: { status: statusFilter, limit: 50 } }),
  });

  const approveM = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/payments/admin/${id}/approve`, { note: 'تم التحقق من الإيصال' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payments'] }),
  });
  const rejectM = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/payments/admin/${id}/reject`, { note: prompt('سبب الرفض:') ?? 'مرفوض' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payments'] }),
  });

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
            <option value="">كل الحالات</option>
            <option value="REQUIRES_REVIEW">⏳ بانتظار المراجعة</option>
            <option value="PENDING">في الانتظار</option>
            <option value="SUCCEEDED">ناجحة</option>
            <option value="FAILED">فاشلة</option>
          </select>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <Th>المرجع</Th><Th>المستخدم</Th><Th>المبلغ</Th><Th>الوسيلة</Th><Th>الغرض</Th><Th>الحالة</Th><Th>التاريخ</Th><Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">لا توجد معاملات</td></tr>
              ) : data.data.map((p) => (
                <tr key={p.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 font-mono text-xs" dir="ltr">{p.reference}</td>
                  <td className="px-4 py-3 text-xs text-gray-500" dir="ltr">{p.userId.slice(0, 8)}…</td>
                  <td className="px-4 py-3 font-bold">{Number(p.total).toLocaleString('en-US')} ر.س</td>
                  <td className="px-4 py-3 text-xs">{p.channel}</td>
                  <td className="px-4 py-3 text-xs">{p.purpose}</td>
                  <td className="px-4 py-3"><Badge color={STATUS_COLOR[p.status] ?? 'navy'}>{p.status}</Badge></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(p.initiatedAt).toLocaleString('ar-SA')}</td>
                  <td className="px-4 py-3">
                    {p.status === 'REQUIRES_REVIEW' && (
                      <div className="flex gap-1">
                        {p.receiptImage && <a href={p.receiptImage} target="_blank" rel="noreferrer" className="text-xs underline text-navy">إيصال</a>}
                        <Button size="sm" variant="primary" onClick={() => approveM.mutate(p.id)}>✓</Button>
                        <Button size="sm" variant="ghost" onClick={() => rejectM.mutate(p.id)}>✕</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
